import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import { EnrollmentConfiguration } from './useEnrollmentConfiguration'
import client from 'enrollment-platform/api/eps.client'
import { notification } from '@surecompanies/core_components'
import { Person } from 'types/person'
import { extractApiErrorMessage } from 'helpers/api-error-message'
import { RescheduleEnrollmentDatesResult } from './useEnrollmentPeriodDates'

export function useEnrollmentOutstanding(clientPublicKey: string) {
  const queryClient = useQueryClient()

  /* --------------------------------- Methods -------------------------------- */
  const fetchEnrollmentOutstanding = async (
    enrollmentConfiguration: EnrollmentConfiguration
  ) => {
    const response = await client.get<{
      members: Person[]
      total: number
    }>(
      `/customers/${clientPublicKey}/configuration/enrollment/missing-enrollments`,
      {
        params: {
          benefitEffectiveDate: enrollmentConfiguration.benefitEffectiveDate,
          enrollmentStartDate: enrollmentConfiguration.enrollmentStartDate,
          enrollmentEndDate: enrollmentConfiguration.enrollmentEndDate,
        },
      }
    )
    return response.data
  }

  const updateEnrollmentOutstanding = async (
    personIds: string[],
    enrollmentConfiguration: EnrollmentConfiguration
  ): Promise<RescheduleEnrollmentDatesResult> => {
    // Sets the enrollment window and benefit effective date for employees
    // who are missing one for this OEP — a plain write, not a change to an
    // existing window. This deliberately hits its own endpoint rather than
    // the shared /reschedule route: /reschedule's validation (active-window/
    // pause guard, overlap checks) is written for *changing* an existing
    // enrollment and rejects these employees for having no window to
    // change — precisely the case this action is for. See EMS-10168.
    try {
      const { data } = await client.put<string[]>(
        `/customers/${clientPublicKey}/configuration/enrollment/bulk-update-enrollment-dates`,
        {
          personIds,
          benefitEffectiveDate: enrollmentConfiguration.benefitEffectiveDate,
          enrollmentStartDate: enrollmentConfiguration.enrollmentStartDate,
          enrollmentEndDate: enrollmentConfiguration.enrollmentEndDate,
        }
      )
      return { successIds: data, errors: [] }
    } catch (error: any) {
      // extractApiErrorMessage flattens this app's {field, errors}[]
      // validation-error shape into readable text (and falls back to a
      // plain string message) instead of "[object Object]".
      throw new Error(
        extractApiErrorMessage(error, 'Failed to update enrollment dates')
      )
    }
  }

  /* ---------------------------------- Query --------------------------------- */
  const queryEnrollmentOutstanding = (
    enrollmentConfiguration: EnrollmentConfiguration,
    options?: {
      queryOptions?: UseQueryOptions<{
        members: Person[]
        total: number
      }>
      enabled?: boolean
    }
  ) => {
    return useQuery({
      queryKey: [
        'enrollmentOutstanding',
        clientPublicKey,
        {
          benefitEffectiveDate: enrollmentConfiguration.benefitEffectiveDate,
          enrollmentStartDate: enrollmentConfiguration.enrollmentStartDate,
          enrollmentEndDate: enrollmentConfiguration.enrollmentEndDate,
        },
      ],
      queryFn: () => fetchEnrollmentOutstanding(enrollmentConfiguration),
      staleTime: 0,
      enabled: options?.enabled !== false && !!clientPublicKey,
      refetchOnWindowFocus: false,
      ...options?.queryOptions,
    })
  }

  const updateEnrollmentOutstandingMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      RescheduleEnrollmentDatesResult,
      Error,
      {
        personIds: string[]
        enrollmentConfiguration: EnrollmentConfiguration
      }
    >
  }) => {
    return useMutation<
      RescheduleEnrollmentDatesResult,
      Error,
      {
        personIds: string[]
        enrollmentConfiguration: EnrollmentConfiguration
      }
    >({
      mutationFn: ({ personIds, enrollmentConfiguration }) =>
        updateEnrollmentOutstanding(personIds, enrollmentConfiguration),
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ['getEnrollmentConfiguration', clientPublicKey],
        })
        queryClient.invalidateQueries({
          queryKey: [
            'enrollmentOutstanding',
            clientPublicKey,
            variables.enrollmentConfiguration.benefitEffectiveDate,
          ],
        })
      },
      onError: (error) => {
        notification.error({
          message: 'Failed to update enrollment outstanding',
          description: error.message ?? 'Unknown error',
        })
        throw error
      },
      ...options?.mutationOptions,
    })
  }

  return {
    queryEnrollmentOutstanding,
    updateEnrollmentOutstandingMutation,
  }
}
